
import React, { useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';

const heroStyle = {
  backgroundImage: `url('${process.env.PUBLIC_URL}/as-hero-image.jpg')`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  position: 'relative',
  zIndex: 0,
  paddingTop: '10rem',
  paddingBottom: '10rem',
};

const heroOverlayStyle = {
  content: '',
  position: 'absolute',
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  background: 'rgba(13, 18, 34, 0.7)',
  zIndex: -1,
};

const HomePage = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.state && location.state.scrollToId) {
      const element = document.getElementById(location.state.scrollToId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
        // Clear the state to prevent re-scrolling on subsequent renders
        window.history.replaceState({}, document.title);
      }
    }
  }, [location]);

  return (
    <main>
      <section id="hero" style={heroStyle} className="px-4 sm:px-6 lg:px-8 text-center relative z-10">
        <div style={heroOverlayStyle} />
        <div className="max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-7xl font-extrabold leading-tight mb-6 bg-clip-text text-transparent bg-gradient-to-r from-white to-accent-cyan">
            Cerberus ID API: The Definitive Fraud Engine
          </h1>
          <p className="text-xl text-text-medium mb-10 max-w-3xl mx-auto">
            Aletheia Systems provides <strong>NIST IAL2 Data Integrity Solutions</strong> specializing in real-time identity validation to <strong>stop fraudulent FAFSA applicants</strong> and <strong>ghost students</strong> from exploiting financial aid processes.
          </p>
          <Link
            to="/appointment"
            className="text-lg bg-accent-cyan hover:bg-cyan-600 text-[#0d1222] font-bold py-3 px-10 rounded-full transition duration-300 shadow-xl shadow-accent-cyan/40 transform hover:scale-105 inline-block"
          >
            Book Your Demo Today
          </Link>
        </div>
      </section>

      <section className="py-20 bg-secondary/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12 text-white">The Cost of Unverified Applicants & FAFSA Fraud</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-secondary p-8 rounded-xl shadow-2xl border-t-4 border-primary text-center">
              <p className="text-5xl font-extrabold text-primary mb-2">4M+</p>
              <p className="text-text-medium uppercase tracking-wider">Annual College Applicants in the US</p>
            </div>
            <div className="bg-secondary p-8 rounded-xl shadow-2xl border-t-4 border-primary text-center">
              <p className="text-5xl font-extrabold text-primary mb-2">40</p>
              <p className="text-text-medium uppercase tracking-wider">Hours Spent Weekly Dealing with Fraud</p>
            </div>
            <div className="bg-secondary p-8 rounded-xl shadow-2xl border-t-4 border-primary text-center">
              <p className="text-5xl font-extrabold text-primary mb-2">IAL2</p>
              <p className="text-text-medium uppercase tracking-wider">NIST IAL2 Compliance for FAFSA Verification (2025-26 Mandate)</p>
            </div>
          </div>
          <div className="text-center mt-12">
            <p className="text-lg text-text-medium max-w-3xl mx-auto">
              We help your institution minimize the impact of <strong>enrollment fraud</strong> and <strong>ghost students</strong>, ensuring compliance and enabling you to focus on valid applicants.
              <a href="/resources/fraud-whitepaper.pdf" title="Download our whitepaper on FAFSA Identity Fraud Prevention" className="text-accent-cyan hover:text-cyan-400 font-semibold ml-2">Download our Whitepaper.</a>
            </p>
          </div>
        </div>
      </section>

      <section id="products" className="py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-4xl font-extrabold text-center mb-6 text-white">Our Core Products: NIST IAL2 Identity Proofing</h2>
        <p className="text-xl text-text-medium text-center mb-16 max-w-4xl mx-auto">Advanced identity validation and data integrity solutions built for the unique security challenges of Higher Education.</p>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="bg-secondary p-8 md:p-12 rounded-2xl shadow-2xl border border-primary/50 flex flex-col">
            <div className="flex-grow">
              <svg className="w-12 h-12 text-accent-cyan mb-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-4a2 2 0 00-2-2H6a2 2 0 00-2 2v4a2 2 0 002 2z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 9V7a3 3 0 00-3-3m0 0a3 3 0 00-3 3v2m9 0a3 3 0 00-3 3v4a3 3 0 003 3H9a3 3 0 003-3v-4a3 3 0 00-3-3z"></path></svg>
              <h3 className="text-3xl font-bold text-primary mb-4">Cerberus ID API</h3>
              <p className="text-lg text-text-light mb-6">
                The definitive anti-fraud engine for Higher Education Systems. Cerberus ID prevents sophisticated attacks and reduces fraud losses by analyzing risk signals in real-time or batch processing.
              </p>

              <h4 className="text-xl font-semibold mb-3 text-accent-cyan">Key Verification Points:</h4>
              <ul className="space-y-2 text-text-medium list-disc list-inside ml-4">
                <li>Applicant names, addresses, phone numbers, emails, SSNs, and IP addresses.</li>
                <li>Configurable scoring interface: assign weights based on your institution's validation thresholds.</li>
                <li><strong>Cerberus Move Option:</strong> Automatically generates updated addresses when movement is detected.</li>
              </ul>
            </div>
            <div className="mt-8 pt-4 border-t border-gray-700 text-center">
              <a href="#api-docs" className="inline-block bg-primary hover:bg-indigo-600 text-white font-semibold py-2 px-6 rounded-full transition duration-300 shadow-lg shadow-primary/50">
                Read More
              </a>
            </div>
          </div>

          <div className="bg-secondary p-8 md:p-12 rounded-2xl shadow-2xl border border-accent-cyan/50 flex flex-col">
            <div className="flex-grow">
              <svg className="w-12 h-12 text-accent-cyan mb-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.298.608 3.3 0z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path></svg>
              <h3 className="text-3xl font-bold text-primary mb-4">Cerberus Portal</h3>
              <p className="text-lg text-text-light mb-6">
                The direct-access, feature-rich portal that puts full control over your verification scoring and data processing directly in your hands.
              </p>

              <h4 className="text-xl font-semibold mb-3 text-accent-cyan">Key Portal Features:</h4>
              <ul className="space-y-2 text-text-medium list-disc list-inside ml-4">
                <li><strong>Custom Score Configuration:</strong> Customize the weights and importance of verification categories to fine-tune your confidence score calculation.</li>
                <li><strong>Instant Validation:</strong> Perform single, on-demand identity checks for immediate verification results.</li>
                <li><strong>Batch Processing:</strong> Upload files, easily map columns to internal fields, and receive a resulting processed file when complete.</li>
                <li><strong>Comprehensive Reporting:</strong> Access detailed reports, complete with visual graphs, for full oversight of each processed file submission.</li>
              </ul>
            </div>
            <div className="mt-8 pt-4 border-t border-gray-700 text-center">
              <a href="#products" className="inline-block bg-primary hover:bg-indigo-600 text-white font-semibold py-2 px-6 rounded-full transition duration-300 shadow-lg shadow-primary/50">
                Read More
              </a>
            </div>
          </div>

          <div className="bg-secondary p-8 md:p-12 rounded-2xl shadow-2xl border border-accent-cyan/50 flex flex-col">
            <div className="flex-grow">
              <svg className="w-12 h-12 text-accent-cyan mb-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path></svg>
              <h3 className="text-3xl font-bold text-primary mb-4">Cerberus Visual ID</h3>
              <p className="text-lg text-text-light mb-6">
                An advanced biometric service that authenticates identity using live capture and government-issued documents. Essential for <strong>NIST IAL2</strong> Level 2 and remote identity proofing, stopping deepfakes and presentation attacks.
              </p>

              <h4 className="text-xl font-semibold mb-3 text-accent-cyan">Key Verification Components:</h4>
              <ul className="space-y-2 text-text-medium list-disc list-inside ml-4">
                <li><strong>Liveness Detection:</strong> Uses passive and active cues to confirm the user is a live human and not a spoof attempt.</li>
                <li><strong>Document Verification:</strong> Automated OCR and forensic analysis of passports, IDs, and driver's licenses.</li>
                <li><strong>NIST IAL2 Identity Proofing</strong> via Document Verification, Record Checks, and Biometric Verification.</li>
                <li><strong>Biometric Matching:</strong> Compares the facial scan to the document photo for reliable match confirmation.</li>
                <li><strong>Mobile Ready:</strong> Seamless capture experience available via Web and Mobile SDKs for easy integration.</li>
              </ul>
            </div>
            <div className="mt-8 pt-4 border-t border-gray-700 text-center">
              <a href="#products" className="inline-block bg-primary hover:bg-indigo-600 text-white font-semibold py-2 px-6 rounded-full transition duration-300 shadow-lg shadow-primary/50">
                Read More
              </a>
            </div>
          </div>
        </div>
      </section>

      <section id="about" className="py-24 bg-secondary/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-extrabold text-center mb-6 text-white">Why Choose Aletheia Systems for Fraud Prevention?</h2>
          <p className="text-xl text-text-medium text-center mb-16 max-w-4xl mx-auto">
            We deliver trusted identity verification using proprietary, configurable systems built by industry veterans to combat <strong>synthetic and ghost identity fraud</strong>.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-secondary p-8 rounded-xl border-b-4 border-accent-cyan">
              <svg className="w-10 h-10 text-accent-cyan mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 4.016M12 18h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
              <h4 className="text-2xl font-bold mb-3 text-primary">Proprietary AI Anti-Fraud Engine</h4>
              <p className="text-text-medium">
                Our secure, encrypted services cross-reference applicant data against regulated sources in seconds, analyzing hundreds of risk signals in real-time to detect sophisticated fraud schemes.
              </p>
            </div>

            <div className="bg-secondary p-8 rounded-xl border-b-4 border-accent-cyan">
              <svg className="w-10 h-10 text-accent-cyan mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.298.608 3.3 0z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path></svg>
              <h4 className="text-2xl font-bold mb-3 text-primary">Institution-Level Control & UX</h4>
              <p className="text-text-medium">
                Use custom pre-configured validation rules and a configurable scoring interface to maintain full control over admissions data validity and quarantine processes with a <strong>frictionless</strong> student experience.
              </p>
            </div>

            <div className="bg-secondary p-8 rounded-xl border-b-4 border-accent-cyan">
              <svg className="w-10 h-10 text-accent-cyan mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M12 15V7m0 0a3.001 3.001 0 00-2-3 3.001 3.000 0 00-2 3M12 7a3.001 3.001 0 012-3 3.001 3.000 0 012 3"></path></svg>
              <h4 className="text-2xl font-bold mb-3 text-primary">Unparalleled Experience</h4>
              <p className="text-text-medium">
                Leverage the power and knowledge of our team with <strong>over 100 years of combined experience</strong> in data science, integration, and higher education security compliance.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section id="services" className="py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-4xl font-extrabold text-center mb-6 text-white">Developer & Integration Services</h2>
        <p className="text-xl text-text-medium text-center mb-16 max-w-4xl mx-auto">
          We ensure seamless integration of Cerberus ID into your existing infrastructure, offering comprehensive support for all clients.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
          <div className="p-6 bg-secondary rounded-xl shadow-xl hover:shadow-primary/30 transition duration-300">
            <h3 className="text-2xl font-bold text-primary mb-3">24/7 Technical Support</h3>
            <p className="text-text-medium mb-4">
              Our Services Group provides round-the-clock technical support, on-boarding assistance, and "hand-holding" for complex developer integrations.
            </p>
            <span className="inline-block bg-accent-cyan/20 text-accent-cyan text-xs font-semibold px-3 py-1 rounded-full">On-boarding Included</span>
          </div>

          <div className="p-6 bg-secondary rounded-xl shadow-xl hover:shadow-primary/30 transition duration-300">
            <h3 className="text-2xl font-bold text-primary mb-3">Custom ERP/SIS/CRM Integration</h3>
            <p className="text-text-medium mb-4">
              We have extensive experience <strong>seamlessly integrating</strong> Cerberus ID with all <strong>major Student Information Systems (SIS)</strong> and CRM platforms. Our professional services team ensures your data flows correctly.
            </p>
            <span className="inline-block bg-accent-cyan/20 text-accent-cyan text-xs font-semibold px-3 py-1 rounded-full">Professional Services</span>
          </div>

          <div className="p-6 bg-secondary rounded-xl shadow-xl hover:shadow-primary/30 transition duration-300">
            <h3 className="text-2xl font-bold text-primary mb-3">REST API for Developers</h3>
            <p className="text-text-medium mb-4">
              Access our services via robust REST APIs using JSON payloads over TLS (SSL). Full quick start guide and sample documentation provided.
            </p>
            <span className="inline-block bg-accent-cyan/20 text-accent-cyan text-xs font-semibold px-3 py-1 rounded-full">Quick Start Ready</span>
          </div>
        </div>
      </section>
      
      <section id="api-docs" className="py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-4xl font-extrabold text-center mb-6 text-accent-cyan">Developer API Reference: NIST IAL2 Fraud API</h2>
        <p className="text-xl text-text-medium text-center mb-12 max-w-4xl mx-auto">
          Integrate Cerberus ID directly into your application stack with our secure, low-latency RESTful API. Below is a quick overview of the primary verification endpoint required for <strong>NIST IAL2</strong> compliance.
        </p>
        
        <div className="max-w-3xl mx-auto space-y-4">
          <div className="bg-secondary rounded-xl shadow-lg">
            <button id="api-toggle" className="w-full text-left p-6 flex justify-between items-center text-xl font-semibold text-white hover:text-accent-cyan transition duration-300">
              <Link to="/fraud-api" onClick={(e) => e.stopPropagation()} className="hover:underline">/v1/verify/identity Endpoint Details</Link>
              <svg id="api-icon" className="w-6 h-6 transform transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
            </button>
            <div id="api-content" className="px-6 pb-6 hidden">
              <p className="text-text-medium mb-4">
                This endpoint allows real-time identity verification by sending applicant data and receiving a detailed confidence score and fraud analysis. 
                <strong>Request Method: POST</strong>
              </p>
              <div className="bg-[#0d1222] p-4 rounded-lg overflow-x-auto text-sm">
                <pre className="text-text-light">
                  <span className="text-accent-cyan">URL:</span> https://api.aletheiasystems.com/v1/verify/identity
                  <span className="text-accent-cyan">Headers:</span> {`{ "Authorization": "Bearer YOUR_API_KEY", "Content-Type": "application/json" }`}

                                    {/* Example Request Body */}
{`                  {
                      "first_name": "Jane",
                      "last_name": "Doe",
                      "ssn_last_4": "1234",
                      "address": "123 Main St",
                      "email": "jane.doe@example.edu"
                  }`}
                </pre>
              </div>
              <p className="text-text-medium mt-4 text-sm">
                For full documentation on response codes, detailed risk factors, and IAL2 verification flows, please refer to the dedicated PDF documentation provided upon signing up.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section id="contact" className="py-24 bg-primary/20">
        <div id="contact-form-section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          
          <div className="bg-secondary p-8 md:p-12 rounded-2xl shadow-2xl border border-primary/50">
            <h2 className="text-3xl font-bold mb-6 text-white">Ready to Secure Your Admissions?</h2>
            <p className="text-text-medium mb-8">
              Are you ready to ensure your SIS, ERP, and CRM systems are not being abused by fraudsters? Reach out to our team.
            </p>

            <form className="space-y-4">
              <input type="text" placeholder="Full Name *" required className="w-full bg-[#0d1222] border border-gray-700 rounded-md p-3 text-white focus:ring-primary focus:border-primary" />
              <input type="email" placeholder="Email Address *" required className="w-full bg-[#0d1222] border border-gray-700 rounded-md p-3 text-white focus:ring-primary focus:border-primary" />
              <input type="tel" placeholder="Phone Number" className="w-full bg-[#0d1222] border border-gray-700 rounded-md p-3 text-white focus:ring-primary focus:border-primary" />
              <input type="text" placeholder="Subject *" required className="w-full bg-[#0d1222] border border-gray-700 rounded-md p-3 text-white focus:ring-primary focus:border-primary" />
              <textarea placeholder="Message *" required rows="4" className="w-full bg-[#0d1222] border border-gray-700 rounded-md p-3 text-white focus:ring-primary focus:border-primary"></textarea>
              
              <button type="submit" className="w-full bg-primary hover:bg-indigo-600 text-white font-bold py-3 rounded-md transition duration-300 shadow-lg shadow-primary/50">
                Send Message
              </button>
            </form>
            <div id="status-message" className="mt-4 hidden text-center p-3 rounded-md bg-accent-cyan/20 text-accent-cyan">Your message has been sent. Thank you!</div>
          </div>

          <div className="lg:pl-10">
            <h3 className="text-2xl font-bold mb-4 text-white">Get In Touch</h3>
            <p className="text-text-medium mb-8">We're here 24/7 to assist with support, demos, and pricing.</p>
            
            <div className="space-y-6">
              <div className="flex items-start">
                <svg className="w-6 h-6 text-accent-cyan flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path></svg>
                <div className="ml-4">
                  <p className="font-semibold text-white">Office Location</p>
                  <p className="text-text-medium">260 Peachtree Street NW, Atlanta, GA 30303</p>
                </div>
              </div>
              <div className="flex items-start">
                <svg className="w-6 h-6 text-accent-cyan flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path></svg>
                <div className="ml-4">
                  <p className="font-semibold text-white">Email Us</p>
                  <p className="text-text-medium">info@aletheiasystems.com</p>
                </div>
              </div>
              <div className="flex items-start">
                <svg className="w-6 h-6 text-accent-cyan flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path></svg>
                <div className="ml-4">
                  <p className="font-semibold text-white">Call Us</p>
                  <p className="text-text-medium">+1 404 282 7007</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default HomePage;
